import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AccessPlanControllerService } from '../../../../core/generated/api/accessPlanController.service';
import { UpgradeOptionsResponseDTO } from '../../../../core/generated/model/upgradeOptionsResponseDTO';

/**
 * Service responsável pela integração com o endpoint de opções de upgrade de planos.
 * Fornece métodos para buscar opções de upgrade disponíveis para um plano específico.
 */
@Injectable({
  providedIn: 'root'
})
export class UpgradeOptionsService {
  private accessPlanService = inject(AccessPlanControllerService);

  /**
   * Busca opções de upgrade para um plano específico.
   * 
   * @param planId ID do plano atual (deve ser um número positivo e inteiro)
   * @returns Observable com as opções de upgrade disponíveis
   * @throws Error se o planId for inválido
   * @throws HttpErrorResponse em caso de erro na requisição HTTP
   */
  getUpgradeOptions(planId: number): Observable<UpgradeOptionsResponseDTO> {
    // Validação de parâmetro planId
    if (!this.validatePlanId(planId)) {
      return throwError(() => new Error('ID de plano inválido. Deve ser um número positivo e inteiro.'));
    }

    return this.accessPlanService.getUpgradeOptions(planId).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Valida se o planId é um número positivo e inteiro.
   * 
   * @param planId ID do plano a ser validado
   * @returns true se o planId for válido, false caso contrário
   */
  private validatePlanId(planId: number): boolean {
    return typeof planId === 'number' && 
           planId > 0 && 
           Number.isInteger(planId);
  }

  /**
   * Trata erros HTTP retornados pelo backend.
   * Mapeia códigos de status HTTP para mensagens de erro amigáveis.
   * 
   * @param error Erro HTTP retornado pela requisição
   * @returns Observable que emite erro com mensagem apropriada
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Erro desconhecido ao buscar opções de upgrade';

    if (error.error instanceof ErrorEvent) {
      // Erro de rede (client-side ou network error)
      errorMessage = 'Erro de conexão. Verifique sua internet';
    } else {
      // Erro do backend (server-side)
      switch (error.status) {
        case 403:
          errorMessage = 'Você não tem permissão para acessar este plano';
          break;
        case 404:
          errorMessage = 'Plano não encontrado';
          break;
        case 500:
          errorMessage = 'Erro ao buscar opções de upgrade. Tente novamente mais tarde';
          break;
        default:
          errorMessage = `Erro ao buscar opções de upgrade (${error.status})`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }
}
